import type { AvatarId, AvatarPose, EquipmentSlot } from "@nano/contracts";

/**
 * Measured landmarks ("sockets") on each body sprite, per pose. Accessories
 * are fitted to these, not to a slot box: a crown is seated on the top of
 * the skull and sized to the head, glasses are scaled so their lenses land
 * on the pupils, and so on (see `wear.ts` for the item side).
 *
 * `x`/`y` are fractions of the body sprite (0..1 from the top-left), so the
 * same numbers drive Phaser in the city and the DOM preview in the shop.
 * `width` is a fraction of body HEIGHT (bodies differ in width). Values were
 * measured on the magical-toy-v1 sprites; re-measure when body art changes.
 */
export interface Socket {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  /** Degrees, clockwise. For hands: tilt of a held stick from vertical. */
  readonly rotation?: number;
}

export interface HandSocket extends Socket {
  /** Left-hand side of the sprite: held props are mirrored. */
  readonly mirror?: boolean;
  /** The hand sits on the body's outline: props go behind and the hand covers the grip. */
  readonly behind?: boolean;
  /**
   * The hand sits over the body: the prop is drawn in front and the hand,
   * cut out along this outline (wrist to fingertips, fractions of the body
   * sprite), is drawn again on top, so the stick runs through the fist.
   */
  readonly fingers?: readonly (readonly [number, number])[];
}

export interface PoseSockets {
  /** Top of the skull (hair mass, not stray tufts); width = head width. */
  readonly head: Socket;
  /** Midpoint between the pupils; width = pupil distance. Absent from behind. */
  readonly eyes?: Socket & { readonly ear?: { readonly x: number; readonly y: number } };
  /** Just under the chin, centred on the neck; width = neck/collar width. */
  readonly neck: Socket;
  /** Spot on the hair for clips and bows. */
  readonly hair: Socket;
  /** Between the shoulder blades; width = shoulder width. */
  readonly back: Socket;
  readonly hand: HandSocket;
  /** Centre of the figure; width = body height. */
  readonly aura: Socket;
}

/**
 * Light cut-out walk rig (no multi-frame sprite sheets, per spec §2.4):
 * the art pipeline splits each body at a clothing hem into an upper layer
 * plus either two feet (stepping) or a skirt (swaying). Every part keeps the
 * full body canvas, so all layers share one origin.
 */
export interface WalkRig {
  readonly style: "feet" | "skirt";
  /** Cut line as a fraction of body height (just below a hem). */
  readonly split: number;
  /** False when the profile legs overlap in the art and cannot step apart. */
  readonly stepsInProfile?: boolean;
}

export interface ArchetypeDef {
  readonly id: AvatarId;
  readonly name: string;
  readonly description: string;
  readonly starterItemId: string;
  readonly bodyAssets: Record<AvatarPose, string>;
  readonly sockets: Record<AvatarPose, PoseSockets>;
  readonly walkRig: WalkRig;
}

export type WalkPart = "upper" | "foot-l" | "foot-r" | "skirt";

export function walkParts(rig: WalkRig): readonly WalkPart[] {
  return rig.style === "feet" ? ["upper", "foot-l", "foot-r"] : ["upper", "skirt"];
}

export function walkPartAsset(bodyAssetId: string, part: WalkPart): string {
  return `${bodyAssetId}.${part}`;
}

/** Draw order, back to front (spec §6.2). */
export const SLOT_ORDER: readonly EquipmentSlot[] = [
  "aura",
  "back",
  "neck",
  "face",
  "hair_accessory",
  "head",
  "hand",
];

const bodies = (id: AvatarId): Record<AvatarPose, string> => ({
  front: `avatar.${id}.front`,
  side: `avatar.${id}.side`,
  back: `avatar.${id}.back`,
});

export const ARCHETYPES: readonly ArchetypeDef[] = [
  {
    id: "wizard",
    name: "Pháp Sư Nam",
    description: "Pháp sư nhỏ tóc tím, áo choàng sao.",
    starterItemId: "hat-wizard",
    bodyAssets: bodies("wizard"),
    sockets: {
      front: {
        head: { x: 0.48, y: 0.075, width: 0.4 },
        eyes: { x: 0.47, y: 0.305, width: 0.149 },
        neck: { x: 0.48, y: 0.435, width: 0.12 },
        hair: { x: 0.74, y: 0.15, width: 0.4, rotation: 18 },
        back: { x: 0.48, y: 0.47, width: 0.28 },
        hand: { x: 0.93, y: 0.645, width: 1, rotation: 14, behind: true },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
      side: {
        head: { x: 0.52, y: 0.075, width: 0.39 },
        eyes: { x: 0.76, y: 0.32, width: 0.149, ear: { x: 0.43, y: 0.33 } },
        neck: { x: 0.62, y: 0.43, width: 0.11 },
        hair: { x: 0.4, y: 0.14, width: 0.39, rotation: -10 },
        back: { x: 0.4, y: 0.47, width: 0.24 },
        hand: {
          x: 0.395,
          y: 0.672,
          width: 1,
          rotation: 35,
          fingers: [[0.354, 0.609], [0.45, 0.609], [0.471, 0.631], [0.475, 0.65], [0.475, 0.684], [0.467, 0.697], [0.429, 0.695], [0.421, 0.686], [0.412, 0.689], [0.412, 0.705], [0.383, 0.707], [0.358, 0.697], [0.338, 0.697], [0.312, 0.688], [0.3, 0.688], [0.3, 0.664], [0.35, 0.631]],
        },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
      back: {
        head: { x: 0.52, y: 0.075, width: 0.39 },
        neck: { x: 0.5, y: 0.44, width: 0.12 },
        hair: { x: 0.28, y: 0.15, width: 0.39, rotation: -18 },
        back: { x: 0.5, y: 0.47, width: 0.28 },
        hand: { x: 0.06, y: 0.65, width: 1, rotation: -14, mirror: true, behind: true },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
    },
    walkRig: { style: "feet", split: 0.88 },
  },
  {
    id: "princess",
    name: "Công Chúa",
    description: "Công chúa tóc vàng, váy hồng đào.",
    starterItemId: "tiara-pink",
    bodyAssets: bodies("princess"),
    sockets: {
      front: {
        head: { x: 0.5, y: 0.015, width: 0.36 },
        eyes: { x: 0.5, y: 0.243, width: 0.125 },
        neck: { x: 0.5, y: 0.36, width: 0.09 },
        hair: { x: 0.71, y: 0.09, width: 0.36, rotation: 22 },
        back: { x: 0.5, y: 0.43, width: 0.24 },
        hand: { x: 0.875, y: 0.625, width: 1, rotation: 12, behind: true },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
      side: {
        head: { x: 0.5, y: 0.015, width: 0.36 },
        eyes: { x: 0.675, y: 0.245, width: 0.125, ear: { x: 0.45, y: 0.265 } },
        neck: { x: 0.56, y: 0.37, width: 0.085 },
        hair: { x: 0.36, y: 0.09, width: 0.36, rotation: -10 },
        back: { x: 0.42, y: 0.43, width: 0.2 },
        hand: {
          x: 0.397,
          y: 0.611,
          width: 1,
          rotation: 35,
          fingers: [[0.391, 0.559], [0.395, 0.553], [0.474, 0.553], [0.474, 0.572], [0.455, 0.59], [0.462, 0.604], [0.455, 0.65], [0.423, 0.65], [0.415, 0.645], [0.415, 0.635], [0.407, 0.633], [0.379, 0.66], [0.316, 0.664], [0.312, 0.615], [0.328, 0.615], [0.336, 0.611], [0.34, 0.6], [0.372, 0.58]],
        },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
      back: {
        head: { x: 0.49, y: 0.015, width: 0.35 },
        neck: { x: 0.5, y: 0.36, width: 0.09 },
        hair: { x: 0.29, y: 0.09, width: 0.35, rotation: -22 },
        back: { x: 0.5, y: 0.43, width: 0.24 },
        hand: { x: 0.1, y: 0.62, width: 1, rotation: -12, mirror: true, behind: true },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
    },
    walkRig: { style: "skirt", split: 0.66 },
  },
  {
    id: "prince",
    name: "Hoàng Tử",
    description: "Hoàng tử tóc nâu, áo xanh trời.",
    starterItemId: "crown-gold",
    bodyAssets: bodies("prince"),
    sockets: {
      front: {
        head: { x: 0.5, y: 0.035, width: 0.38 },
        eyes: { x: 0.51, y: 0.3, width: 0.14 },
        neck: { x: 0.5, y: 0.425, width: 0.105 },
        hair: { x: 0.75, y: 0.11, width: 0.38, rotation: 18 },
        back: { x: 0.5, y: 0.45, width: 0.27 },
        hand: { x: 0.875, y: 0.69, width: 1, rotation: 12, behind: true },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
      side: {
        head: { x: 0.53, y: 0.03, width: 0.4 },
        eyes: { x: 0.725, y: 0.305, width: 0.14, ear: { x: 0.435, y: 0.305 } },
        neck: { x: 0.6, y: 0.42, width: 0.1 },
        hair: { x: 0.4, y: 0.1, width: 0.4, rotation: -10 },
        back: { x: 0.42, y: 0.45, width: 0.22 },
        hand: {
          x: 0.467,
          y: 0.669,
          width: 1,
          rotation: 35,
          fingers: [[0.423, 0.621], [0.465, 0.621], [0.515, 0.627], [0.548, 0.646], [0.556, 0.664], [0.568, 0.668], [0.573, 0.691], [0.56, 0.697], [0.527, 0.697], [0.523, 0.721], [0.373, 0.723], [0.365, 0.703], [0.353, 0.701], [0.353, 0.66], [0.373, 0.631], [0.39, 0.625]],
        },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
      back: {
        head: { x: 0.52, y: 0.04, width: 0.38 },
        neck: { x: 0.5, y: 0.42, width: 0.105 },
        hair: { x: 0.27, y: 0.11, width: 0.38, rotation: -18 },
        back: { x: 0.5, y: 0.45, width: 0.27 },
        hand: { x: 0.14, y: 0.69, width: 1, rotation: -12, mirror: true, behind: true },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
    },
    walkRig: { style: "feet", split: 0.745, stepsInProfile: false },
  },
  {
    id: "sorceress",
    name: "Pháp Sư Nữ",
    description: "Nữ pháp sư tóc tím tết bím, váy choàng sao.",
    starterItemId: "hat-sorceress",
    bodyAssets: bodies("sorceress"),
    sockets: {
      front: {
        head: { x: 0.5, y: 0.03, width: 0.42 },
        eyes: { x: 0.49, y: 0.3, width: 0.175 },
        neck: { x: 0.5, y: 0.42, width: 0.1 },
        hair: { x: 0.72, y: 0.12, width: 0.42, rotation: 20 },
        back: { x: 0.5, y: 0.47, width: 0.26 },
        hand: { x: 0.9, y: 0.655, width: 1, rotation: 12, behind: true },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
      side: {
        head: { x: 0.55, y: 0.02, width: 0.38 },
        eyes: { x: 0.68, y: 0.3, width: 0.175, ear: { x: 0.37, y: 0.31 } },
        neck: { x: 0.6, y: 0.41, width: 0.09 },
        hair: { x: 0.38, y: 0.12, width: 0.38, rotation: -10 },
        back: { x: 0.4, y: 0.47, width: 0.2 },
        hand: {
          x: 0.311,
          y: 0.674,
          width: 1,
          rotation: 35,
          fingers: [[0.329, 0.615], [0.387, 0.615], [0.41, 0.631], [0.41, 0.689], [0.374, 0.691], [0.356, 0.686], [0.324, 0.705], [0.27, 0.707], [0.266, 0.703], [0.252, 0.703], [0.248, 0.691], [0.234, 0.688], [0.234, 0.67], [0.266, 0.645]],
        },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
      back: {
        head: { x: 0.5, y: 0.02, width: 0.42 },
        neck: { x: 0.5, y: 0.42, width: 0.1 },
        hair: { x: 0.28, y: 0.12, width: 0.42, rotation: -20 },
        back: { x: 0.5, y: 0.47, width: 0.26 },
        hand: { x: 0.05, y: 0.64, width: 1, rotation: -12, mirror: true, behind: true },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
    },
    walkRig: { style: "skirt", split: 0.7 },
  },
  {
    id: "angel-boy",
    name: "Thiên Thần Nam",
    description: "Thiên thần nhỏ tóc xoăn vàng, áo trắng viền vàng.",
    starterItemId: "halo-gold",
    bodyAssets: bodies("angel-boy"),
    sockets: {
      front: {
        head: { x: 0.5, y: 0.02, width: 0.46 },
        eyes: { x: 0.485, y: 0.36, width: 0.17 },
        neck: { x: 0.5, y: 0.47, width: 0.1 },
        hair: { x: 0.75, y: 0.12, width: 0.46, rotation: 18 },
        back: { x: 0.5, y: 0.52, width: 0.28 },
        hand: { x: 0.88, y: 0.71, width: 1, rotation: 12, behind: true },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
      side: {
        head: { x: 0.5, y: 0.02, width: 0.46 },
        eyes: { x: 0.71, y: 0.36, width: 0.17, ear: { x: 0.4, y: 0.38 } },
        neck: { x: 0.58, y: 0.47, width: 0.09 },
        hair: { x: 0.35, y: 0.12, width: 0.46, rotation: -10 },
        back: { x: 0.4, y: 0.52, width: 0.22 },
        hand: {
          x: 0.519,
          y: 0.71,
          width: 1,
          rotation: 35,
          fingers: [[0.465, 0.654], [0.465, 0.648], [0.587, 0.648], [0.595, 0.672], [0.625, 0.686], [0.625, 0.723], [0.636, 0.727], [0.636, 0.738], [0.543, 0.773], [0.483, 0.77], [0.442, 0.754], [0.431, 0.744], [0.431, 0.73], [0.442, 0.729], [0.439, 0.684], [0.465, 0.676]],
        },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
      back: {
        head: { x: 0.5, y: 0.02, width: 0.46 },
        neck: { x: 0.5, y: 0.47, width: 0.1 },
        hair: { x: 0.27, y: 0.12, width: 0.46, rotation: -18 },
        back: { x: 0.5, y: 0.52, width: 0.28 },
        hand: { x: 0.08, y: 0.71, width: 1, rotation: -12, mirror: true, behind: true },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
    },
    walkRig: { style: "feet", split: 0.87, stepsInProfile: false },
  },
  {
    id: "angel-girl",
    name: "Thiên Thần Nữ",
    description: "Thiên thần nhỏ tóc bạch kim, váy trắng viền vàng.",
    starterItemId: "halo-gold",
    bodyAssets: bodies("angel-girl"),
    sockets: {
      front: {
        head: { x: 0.5, y: 0.02, width: 0.39 },
        eyes: { x: 0.48, y: 0.3, width: 0.14 },
        neck: { x: 0.5, y: 0.4, width: 0.09 },
        hair: { x: 0.72, y: 0.1, width: 0.39, rotation: 22 },
        back: { x: 0.5, y: 0.46, width: 0.24 },
        hand: { x: 0.82, y: 0.655, width: 1, rotation: 12, behind: true },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
      side: {
        head: { x: 0.55, y: 0.03, width: 0.4 },
        eyes: { x: 0.7, y: 0.3, width: 0.14, ear: { x: 0.52, y: 0.3 } },
        neck: { x: 0.6, y: 0.4, width: 0.085 },
        hair: { x: 0.42, y: 0.1, width: 0.4, rotation: -10 },
        back: { x: 0.45, y: 0.46, width: 0.2 },
        hand: {
          x: 0.49,
          y: 0.668,
          width: 1,
          rotation: 35,
          fingers: [[0.485, 0.629], [0.537, 0.629], [0.566, 0.639], [0.577, 0.65], [0.57, 0.691], [0.529, 0.691], [0.518, 0.678], [0.471, 0.697], [0.415, 0.695], [0.408, 0.682], [0.397, 0.678], [0.401, 0.662]],
        },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
      back: {
        head: { x: 0.5, y: 0.02, width: 0.39 },
        neck: { x: 0.5, y: 0.4, width: 0.09 },
        hair: { x: 0.28, y: 0.1, width: 0.39, rotation: -22 },
        back: { x: 0.5, y: 0.46, width: 0.24 },
        hand: { x: 0.12, y: 0.655, width: 1, rotation: -12, mirror: true, behind: true },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
    },
    walkRig: { style: "skirt", split: 0.7 },
  },
  {
    id: "demon-boy",
    name: "Ác Quỷ Nam",
    description: "Tiểu quỷ tinh nghịch tóc mận, cánh dơi và đuôi tim.",
    starterItemId: "horns-imp",
    bodyAssets: bodies("demon-boy"),
    sockets: {
      front: {
        head: { x: 0.5, y: 0.05, width: 0.44 },
        eyes: { x: 0.47, y: 0.37, width: 0.16 },
        neck: { x: 0.5, y: 0.5, width: 0.1 },
        hair: { x: 0.74, y: 0.15, width: 0.44, rotation: 18 },
        back: { x: 0.5, y: 0.55, width: 0.28 },
        hand: { x: 0.88, y: 0.75, width: 1, rotation: 12, behind: true },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
      side: {
        head: { x: 0.52, y: 0.05, width: 0.44 },
        eyes: { x: 0.68, y: 0.37, width: 0.16, ear: { x: 0.4, y: 0.4 } },
        neck: { x: 0.58, y: 0.5, width: 0.09 },
        hair: { x: 0.38, y: 0.14, width: 0.44, rotation: -10 },
        back: { x: 0.42, y: 0.56, width: 0.22 },
        hand: {
          x: 0.54,
          y: 0.758,
          width: 1,
          rotation: 35,
          fingers: [[0.502, 0.707], [0.573, 0.707], [0.592, 0.711], [0.648, 0.752], [0.644, 0.77], [0.596, 0.77], [0.596, 0.785], [0.584, 0.797], [0.558, 0.797], [0.551, 0.789], [0.491, 0.789], [0.476, 0.781], [0.453, 0.781], [0.449, 0.754], [0.461, 0.721], [0.472, 0.713]],
        },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
      back: {
        head: { x: 0.5, y: 0.05, width: 0.44 },
        neck: { x: 0.5, y: 0.5, width: 0.1 },
        hair: { x: 0.28, y: 0.15, width: 0.44, rotation: -18 },
        back: { x: 0.5, y: 0.55, width: 0.28 },
        hand: { x: 0.09, y: 0.76, width: 1, rotation: -12, mirror: true, behind: true },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
    },
    walkRig: { style: "feet", split: 0.88, stepsInProfile: false },
  },
  {
    id: "demon-girl",
    name: "Ác Quỷ Nữ",
    description: "Tiểu quỷ tinh nghịch tóc búi đôi, váy đỏ mận.",
    starterItemId: "horns-imp",
    bodyAssets: bodies("demon-girl"),
    sockets: {
      front: {
        head: { x: 0.5, y: 0.07, width: 0.42 },
        eyes: { x: 0.455, y: 0.36, width: 0.18 },
        neck: { x: 0.5, y: 0.5, width: 0.09 },
        hair: { x: 0.72, y: 0.2, width: 0.42, rotation: 22 },
        back: { x: 0.5, y: 0.54, width: 0.24 },
        hand: { x: 0.87, y: 0.68, width: 1, rotation: 12, behind: true },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
      side: {
        head: { x: 0.55, y: 0.07, width: 0.42 },
        eyes: { x: 0.68, y: 0.36, width: 0.18, ear: { x: 0.39, y: 0.39 } },
        neck: { x: 0.58, y: 0.5, width: 0.085 },
        hair: { x: 0.35, y: 0.2, width: 0.42, rotation: -10 },
        back: { x: 0.42, y: 0.55, width: 0.2 },
        hand: {
          x: 0.409,
          y: 0.669,
          width: 1,
          rotation: 35,
          fingers: [[0.404, 0.6], [0.408, 0.594], [0.502, 0.594], [0.51, 0.598], [0.51, 0.613], [0.494, 0.643], [0.459, 0.67], [0.467, 0.691], [0.478, 0.697], [0.478, 0.711], [0.443, 0.713], [0.427, 0.709], [0.42, 0.701], [0.369, 0.723], [0.306, 0.719], [0.298, 0.715], [0.298, 0.707], [0.286, 0.705], [0.286, 0.689], [0.365, 0.652], [0.384, 0.615]],
        },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
      back: {
        head: { x: 0.5, y: 0.07, width: 0.42 },
        neck: { x: 0.5, y: 0.5, width: 0.09 },
        hair: { x: 0.28, y: 0.2, width: 0.42, rotation: -22 },
        back: { x: 0.5, y: 0.54, width: 0.24 },
        hand: { x: 0.14, y: 0.68, width: 1, rotation: -12, mirror: true, behind: true },
        aura: { x: 0.5, y: 0.55, width: 1 },
      },
    },
    walkRig: { style: "feet", split: 0.9, stepsInProfile: false },
  },
];

export const ARCHETYPE_BY_ID: ReadonlyMap<AvatarId, ArchetypeDef> = new Map(
  ARCHETYPES.map((archetype) => [archetype.id, archetype]),
);
